/**
 * TopupCoinDetailPage — /parent/topup-coins (ROUTES.PARENT_TOPUP_COINS).
 *
 * Implements TS-017: parent-only detail screen listing every non-expired
 * top-up coin batch (remaining, SGT expiry date, source, SGT granted date).
 * Opened from the "Top-up Coins" tile inside the Overview tab's WalletPanel
 * (CoinBalancePanels.onTopupDetail -> WalletPanel.onTopupDetail -> here).
 *
 * Modified BUG-20260703-193037 (2026-07-03): ALSO renders within-retention-
 * window EXPIRED batches (coin_config.topupRetentionDays, default 90 days
 * post-expiry) as a de-emphasized "Expired on <date>" section below the
 * active batches. Partitioning is by the backend-provided `status` field
 * only — no client-side re-derivation of expiry (TO-LL-003 / CCM-05).
 * totalRemaining stays active-only (unchanged headline balance semantics).
 *
 * Data: useTopupCoins() (TS-013) — GET /parent/topup-coins (API-003, TS-007).
 * TO-LL-003 / CCM-05: batches are rendered EXACTLY as returned by the hook —
 * no client-side re-sort or re-filter. The backend orders active batches
 * soonest-expiry-first, then within-window expired batches most-recently-
 * expired-first (AC-TS017.3).
 * TO-LL-036: fetch errors are never swallowed — surfaced via ErrorState with
 * a retry action wired to the hook's refetch().
 * TO-LL-044: reads TopupBatch's exact camelCase keys (batchId, remaining,
 * grantedAt, expiresAt, source) — no renamed/derived keys.
 *
 * Route registration: App.tsx, inside the existing <ParentGuard> block
 * (CCM-03 — client-side UX guard; the backend enforces parent-only via
 * require_group, returning 403 for children, API-003).
 */

import { useNavigate } from 'react-router-dom';
import { useTopupCoins } from '@/hooks/useTopupCoins';
import { LoadingState } from '@/components/LoadingState';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { Card, CardContent } from '@/components/ui/card';
import { AppLogo } from '@/components/AppLogo';
import { LogoutButton } from '@/components/LogoutButton';
import { ROUTES } from '@/constants/routes';
import otterCoinSvg from '@/assets/otter-coin.svg';
import { ArrowLeft } from 'lucide-react';
import type { TopupBatch } from '@/types/coins';

// ─── SGT date formatter ────────────────────────────────────────────────────
// TS-017 (T3): copied/adapted from the proven pattern in CoinBalancePanels.tsx
// (lines ~199-216, TS-014). expiresAt/grantedAt are ISO-8601 UTC timestamps
// (API-003) — the Asia/Singapore pin is mandatory so a UTC timestamp never
// day-shifts in a non-SGT runtime (same reasoning as BR-TS014.1).
function formatSgtDate(iso: string): string | null {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleDateString('en-SG', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      timeZone: 'Asia/Singapore', // required — pins SGT so UTC timestamps don't day-shift
    });
  } catch {
    return null;
  }
}

// ─── Source friendly-label mapper ──────────────────────────────────────────
// TS-017 (T4): TopupBatch.source is typed as `string` (not a strict union) on
// purpose (coins.ts) — any unrecognised value falls back to a neutral label
// (API-003 edge case, AC-TS017.2 "unknown -> neutral label"). Never render a
// raw backend enum value to the parent.
const SOURCE_LABELS: Record<string, string> = {
  purchase: 'Purchase',
  signup_bonus: 'Welcome bonus',
  welcome: 'Welcome bonus', // defensive alias — not emitted today, kept for safety
  referral: 'Referral reward',
  // promo-code US-003 (T5) / AC-003.1: a source='promo' batch (granted by
  // PromoGrantService, backend/src/services/promo_grant_service.py) renders
  // 'Bonus' instead of the generic 'Top-up' fallback.
  promo: 'Bonus',
};
function sourceLabel(source: string): string {
  return SOURCE_LABELS[source] ?? 'Top-up';
}

// ─── TopupBatchRow ──────────────────────────────────────────────────────────

interface TopupBatchRowProps {
  batch: TopupBatch;
  /**
   * BUG-20260703-193037: renders a de-emphasized "Expired on <date>" variant
   * when true. Defaults to false (active row, unchanged from before).
   */
  isExpired?: boolean;
}

/**
 * A single top-up batch row — remaining, SGT expiry/expired date, friendly
 * source, SGT granted date. Rendered as-is in the order supplied by the hook
 * (TO-LL-003 / CCM-05 — never re-sorted or re-filtered here).
 *
 * Modified BUG-20260703-193037 (2026-07-03): the `isExpired` prop switches to
 * a de-emphasized (muted/greyed) presentation with an "Expired on <date>"
 * label instead of "Expires <date>".
 */
function TopupBatchRow({ batch, isExpired = false }: TopupBatchRowProps) {
  const dateLabel = formatSgtDate(batch.expiresAt);
  const grantedLabel = formatSgtDate(batch.grantedAt);

  return (
    <Card
      className={
        isExpired
          ? 'rounded-2xl border-gray-200 bg-gray-50 opacity-70'
          : 'rounded-2xl border-otter-sand'
      }
    >
      <CardContent className="flex flex-col gap-3 p-4 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-3">
          <img
            src={otterCoinSvg}
            alt="Otter Coin"
            className={
              isExpired ? 'h-8 w-8 flex-shrink-0 grayscale' : 'h-8 w-8 flex-shrink-0 drop-shadow-sm'
            }
            aria-hidden="true"
          />
          <div>
            <span
              className={
                isExpired
                  ? 'font-display block text-2xl font-bold leading-none text-gray-400'
                  : 'font-display block text-2xl font-bold leading-none text-orange-600'
              }
              aria-label={`${batch.remaining} coins ${isExpired ? 'expired' : 'remaining'}`}
            >
              {batch.remaining}
            </span>
            <span className="text-xs font-semibold text-gray-500">{sourceLabel(batch.source)}</span>
          </div>
        </div>
        <div className="flex flex-col gap-0.5 text-sm text-gray-600 md:items-end">
          {dateLabel && isExpired && (
            <span className="text-gray-500">
              Expired on <span className="font-semibold text-gray-600">{dateLabel}</span>
            </span>
          )}
          {dateLabel && !isExpired && (
            <span>
              Expires <span className="font-semibold text-gray-800">{dateLabel}</span>
            </span>
          )}
          {grantedLabel && <span className="text-xs text-gray-400">Granted {grantedLabel}</span>}
        </div>
      </CardContent>
    </Card>
  );
}

// ─── TopupCoinDetailPage (main export) ─────────────────────────────────────

export function TopupCoinDetailPage() {
  const { batches, totalRemaining, isLoading, error, refetch } = useTopupCoins();
  const navigate = useNavigate();

  return (
    <div className="otter-bg font-body min-h-screen text-otter-earth-800">
      {/* Page chrome — reuses the ParentDashboardPage header pattern (AppLogo + LogoutButton) */}
      <header className="sticky top-0 z-20 flex items-center justify-between border-b border-otter-sand bg-otter-paper/80 px-3 py-2 backdrop-blur-md md:px-6 md:py-3">
        <AppLogo />
        <LogoutButton />
      </header>

      <main className="mx-auto max-w-3xl px-4 pb-8 pt-4 md:px-6 md:pt-7">
        <button
          type="button"
          onClick={() => navigate(ROUTES.PARENT_DASHBOARD)}
          className="mb-4 inline-flex h-11 items-center gap-1.5 rounded-lg px-2 text-sm font-semibold text-pebbles-500 transition-colors hover:bg-otter-sand/40 hover:text-otter-earth-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-otter-orange-400"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Back to dashboard
        </button>

        <h1 className="font-display mb-1 text-2xl font-semibold leading-tight text-otter-earth-800 md:text-[32px]">
          Top-up Coins
        </h1>
        <p className="mb-6 text-sm font-medium text-gray-500 md:text-[15px]">
          {/* BUG-20260703-193037 (T-7, non-optional copy update): recently-
              expired batches are now shown too, within the retention window. */}
          Your top-up batches, including recently expired ones.
        </p>

        {/* Loading state (AC-TS017.2 precondition) */}
        {isLoading && <LoadingState message="Loading your coin details..." />}

        {/* Error state — never swallowed (TO-LL-036); exact copy per story edge case */}
        {!isLoading && error && (
          <ErrorState
            title="Could not load your coin details"
            message="We couldn't load your coin details right now. Please try again."
            onRetry={refetch}
          />
        )}

        {/* Empty state (AC-TS017.5) */}
        {!isLoading && !error && batches.length === 0 && (
          <EmptyState message="No top-up coins yet." />
        )}

        {/* Success state — render as-is, no client sort/filter (AC-TS017.2/.3).
            BUG-20260703-193037: partition by the backend-provided `status`
            field only (no re-derivation of expiry, no re-sort — TO-LL-003 /
            CCM-05 / D-4). Intra-group order is preserved exactly as
            returned by the backend. */}
        {!isLoading && !error && batches.length > 0 && (
          <div>
            <p className="mb-4 text-sm font-semibold text-gray-700">
              Top-up coins remaining: <span className="text-orange-600">{totalRemaining}</span>
            </p>
            <div className="space-y-3">
              {batches
                .filter((b) => b.status !== 'expired')
                .map((b) => (
                  <TopupBatchRow key={b.batchId} batch={b} />
                ))}
            </div>

            {batches.some((b) => b.status === 'expired') && (
              <div className="mt-6">
                <h2 className="mb-3 text-sm font-semibold text-gray-500">Expired</h2>
                <div className="space-y-3">
                  {batches
                    .filter((b) => b.status === 'expired')
                    .map((b) => (
                      <TopupBatchRow key={b.batchId} batch={b} isExpired />
                    ))}
                </div>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
